/**
 * API Sequence Types
 *
 * Types for configuring and executing API sequence nodes in workflows.
 * An API sequence node executes multiple API calls in order, with support
 * for template variables that reference previous step responses.
 */

import { z } from 'zod';

/**
 * Comparison operators shared by API-sequence validations and deterministic
 * workflow condition predicates. Single source of truth — both the validation
 * schemas and the condition predicate schema derive from this enum.
 */
export const ValidationOperatorSchema = z.enum([
  'equals',
  'not_equals',
  'gt',
  'lt',
  'gte',
  'lte',
  'contains',
  'not_contains',
  'is_set',
  'is_not_set',
]);

export type ValidationOperator = z.infer<typeof ValidationOperatorSchema>;

/**
 * Pure operator evaluation shared by API-sequence validations and deterministic
 * workflow conditions. `actual` / `expected` are pre-resolved string values
 * (`undefined` means "not set"). Returns whether the comparison passed plus a
 * human-readable message. Numeric operators coerce both operands via `Number()`;
 * non-numeric input fails with a descriptive message rather than throwing.
 */
export function evaluateOperator(args: {
  actual: string | undefined;
  operator: ValidationOperator;
  expected?: string;
  /** Label for the left operand in messages (e.g. a ref or variable name). */
  label?: string;
}): { passed: boolean; message: string } {
  const { actual, operator, expected } = args;
  const label = args.label ?? 'value';
  const isSet = actual !== undefined && actual !== null;

  switch (operator) {
    case 'is_set':
      return {
        passed: isSet,
        message: isSet ? `${label} is set (value: ${actual})` : `${label} is not set`,
      };
    case 'is_not_set':
      return {
        passed: !isSet,
        message: !isSet ? `${label} is not set` : `${label} is set (value: ${actual})`,
      };
    case 'equals':
      return {
        passed: actual === expected,
        message:
          actual === expected
            ? `${label} equals "${expected}"`
            : `Expected ${label} to equal "${expected}", got "${actual}"`,
      };
    case 'not_equals':
      return {
        passed: actual !== expected,
        message:
          actual !== expected
            ? `${label} does not equal "${expected}"`
            : `Expected ${label} to not equal "${expected}"`,
      };
    case 'contains': {
      const pass =
        isSet && expected !== undefined && expected !== null && actual.includes(expected);
      return {
        passed: pass,
        message: pass
          ? `${label} contains "${expected}"`
          : `Expected ${label} to contain "${expected}", got "${actual}"`,
      };
    }
    case 'not_contains': {
      const pass =
        !isSet || expected === undefined || expected === null || !actual.includes(expected);
      return {
        passed: pass,
        message: pass
          ? `${label} does not contain "${expected}"`
          : `Expected ${label} to not contain "${expected}", got "${actual}"`,
      };
    }
    case 'gt':
    case 'lt':
    case 'gte':
    case 'lte': {
      const numActual = Number(actual);
      const numExpected = Number(expected);
      if (isNaN(numActual) || isNaN(numExpected)) {
        return {
          passed: false,
          message: `Cannot compare non-numeric values: "${actual}" ${operator} "${expected}"`,
        };
      }
      const ops = {
        gt: (a: number, b: number) => a > b,
        lt: (a: number, b: number) => a < b,
        gte: (a: number, b: number) => a >= b,
        lte: (a: number, b: number) => a <= b,
      } as const;
      const passed = ops[operator](numActual, numExpected);
      return {
        passed,
        message: passed
          ? `${label} (${numActual}) ${operator} ${numExpected}`
          : `Expected ${label} (${numActual}) ${operator} ${numExpected}`,
      };
    }
    default:
      return { passed: false, message: `Unknown operator: ${operator as string}` };
  }
}

export interface ApiSequenceValidation {
  /** Reference to a step value: "stepId.path.to.value" (e.g., "step3.data.accountId") */
  left: string;
  operator: ValidationOperator;
  /** Literal value or step reference. Not needed for is_set/is_not_set. */
  right?: string;
  description?: string;
}

export interface ApiSequenceValidationResult {
  validation: ApiSequenceValidation;
  passed: boolean;
  actualValue?: string;
  expectedValue?: string;
  message?: string;
}

/**
 * A single step in an API sequence.
 */
export interface ApiSequenceStep {
  /**
   * Unique identifier for this step (used for variable references).
   * If not provided, defaults to `step{index}`.
   */
  stepId?: string;

  /**
   * Reference to an API request in the api_requests table.
   * Provides default method, URL pattern, and example request/response data.
   * Empty string indicates a custom step whose request shape comes from
   * method/urlTemplate/bodyTemplate/headersTemplate instead of the catalog.
   */
  apiRequestId: string;

  /**
   * Which credential to use for this request.
   * If null, the request is made without authentication.
   */
  credentialId: string | null;

  /**
   * Authenticate via a credential template/persona resolved against the run's
   * environment (mirrors a login node's `credentialSource: from_template`).
   * Takes precedence over `credentialId` when set — the executor resolves it to
   * the concrete credential bound for the environment (e.g. the sandbox-minted
   * credential), so an api_sequence isn't pinned to one static per-env id.
   */
  credentialTemplateId?: string | null;

  /**
   * Author-facing name of the credential template/persona. Resolved to
   * `credentialTemplateId` server-side on save (and populated from the id on
   * pull) so a `.cofactor.md` sidecar can name the persona instead of a raw uuid.
   * Not read at execution time — the executor uses `credentialTemplateId`.
   */
  credentialTemplateName?: string | null;

  /**
   * Override the HTTP method (GET, POST, PUT, PATCH, DELETE, etc.).
   * If not specified, uses the method from the referenced API request.
   */
  method?: string;

  /**
   * URL template with optional variable placeholders.
   * Supports {{stepN.response.path.to.value}} syntax.
   * If not specified, backfilled from the API catalog at execution time.
   *
   * @example "https://api.example.com/users/{{step1.response.data.userId}}"
   */
  urlTemplate?: string;

  /**
   * Query parameters as key-value pairs. Values support template variables.
   * These are merged into the URL at execution time (appended to any existing query string).
   */
  queryParams?: Array<{ key: string; value: string }>;

  /**
   * Request body template (JSON string) with optional variable placeholders.
   * Supports {{stepN.response.path.to.value}} syntax.
   * If null/undefined, uses the sample from the API request for POST/PUT/PATCH,
   * or sends no body for GET/DELETE.
   */
  bodyTemplate?: string | null;

  /**
   * Request header overrides with optional variable placeholders.
   * These are merged with headers from the credential and API request sample.
   */
  headersTemplate?: Record<string, string> | null;

  /**
   * Name to store this step's response under for use in subsequent steps.
   * Defaults to `step{index}` if not specified.
   *
   * @example "createUserResponse"
   */
  responseVariableName?: string;

  /**
   * Expected HTTP status codes for validation.
   * If specified, the step fails if the response status is not in this list.
   * If not specified, any successful response (2xx) is accepted.
   *
   * @example [200, 201]
   */
  expectedStatusCodes?: number[];

  /**
   * Optional description for this step (for UI display).
   */
  description?: string;

  /**
   * Assertions to run after this step's HTTP request completes.
   * Each validation checks a value from step results against a condition.
   */
  validations?: ApiSequenceValidation[];
}

/**
 * A variable export declaration that extracts a value from a step's response
 * and registers it in the workflow's variable registry for downstream steps.
 */
export interface ApiSequenceExport {
  /** Variable name using resource_field convention (e.g., "project_id", "project_name", "user_email") */
  variableName: string;
  /** Which step's response to extract from */
  stepId: string;
  /** Dot-notation path in the JSON body (e.g., "data.id") */
  responsePath: string;
  /** Whether to extract from the request body or response body. Defaults to 'response'. */
  source?: 'request' | 'response';
  /** Description for UI display */
  description?: string;
}

/**
 * Configuration for an API sequence workflow node.
 */
export interface ApiSequenceConfig {
  /**
   * Ordered list of API calls to execute.
   */
  steps: ApiSequenceStep[];

  /**
   * Human-readable description of what this sequence does.
   */
  description?: string;

  /**
   * If true, stop execution on the first failed step.
   * If false, continue executing remaining steps even if one fails.
   * Defaults to true.
   */
  stopOnFailure?: boolean;

  /**
   * Variable exports to extract from step responses and register
   * in the workflow's variable registry for downstream nodes.
   */
  exports?: ApiSequenceExport[];
}

/**
 * Result of executing a single API sequence step.
 */
export interface ApiSequenceStepResult {
  stepId: string;
  success: boolean;
  statusCode?: number;
  statusText?: string;
  headers?: Record<string, string>;
  body?: string | null;
  durationMs?: number;
  error?: string;
  timestamp: string;
  /** The fully resolved URL that was actually requested */
  resolvedUrl?: string;
  /** The HTTP method used */
  method?: string;
  /** The request body sent (if any) */
  requestBody?: string | null;
  /** The request headers sent */
  requestHeaders?: Record<string, string>;
  /** Results of validation assertions run after this step */
  validationResults?: ApiSequenceValidationResult[];
}

/**
 * Verification result comparing declared seed outputs against actually exported variables.
 */
export interface SeedOutputVerification {
  /** Whether all declared seed outputs were satisfied by exports */
  allSatisfied: boolean;
  /** dataKeys that were found in exportedVariables */
  satisfied: string[];
  /** dataKeys that were declared but missing from exportedVariables */
  missing: string[];
}

/**
 * How an api_sequence_runs row was initiated. Drives the "trigger" chip in the
 * flow designer's Runs panel and lets us distinguish manual designer-button
 * runs from runs spawned by a workflow execution or test suite.
 */
export type ApiSequenceTriggeredVia = 'designer' | 'workflow' | 'seed' | 'test-suite';

/**
 * Outbound HTTP header names attached to every request issued by an
 * api_sequence step (and to Playwright browser contexts when the run
 * originates from a workflow). Together they reconstruct a deep link to the
 * failing step:
 *   `https://app.trycanary.ai/flows/{flowId}?runId={runId}`
 *
 * NOTE: these headers are sent to **third-party hosts** that the step targets.
 * Values are opaque UUIDs (workflows.id / workflow_runs.id) — no credentials,
 * no PII — but be aware they cross our perimeter intentionally so customers
 * can correlate canary traffic in their own server logs.
 */
export const CANARY_TRACE_HEADERS = {
  flowId: 'x-canary-flow-id',
  runId: 'x-canary-run-id',
} as const;

/**
 * Build the canary trace header pair from a run's identifiers. Returns an
 * empty object when no context is available so callers can spread freely.
 */
export function buildCanaryTraceHeaders(
  context: { flowId: string; runId: string } | undefined
): Record<string, string> {
  if (!context) return {};
  return {
    [CANARY_TRACE_HEADERS.flowId]: context.flowId,
    [CANARY_TRACE_HEADERS.runId]: context.runId,
  };
}

/**
 * Result of executing an entire API sequence.
 */
export interface ApiSequenceResult {
  success: boolean;
  stepResults: ApiSequenceStepResult[];
  totalDurationMs: number;
  failedStepId?: string;
  /** Variables extracted from step responses via the exports config */
  exportedVariables?: Record<string, string>;
  /** Verification of declared seed outputs against actual exports (seed workflows only) */
  seedOutputVerification?: SeedOutputVerification;
}
